// React
import React, { useEffect, useRef, useState } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/sharp-regular-svg-icons";
import { faFilter as fasFilter, faSortAmountDown, faSortAmountUp, faCheckSquare, faSquare, faSort } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { DropdownDirection, DropdownPosition, useDropdownPanel, DropdownAlignment, DropdownPanel } from "../dropdown-panel/dropdown-panel";
import { getDataTestId } from "../functions/data-test-ids";
import { FontAwesome } from "../icons/icons";
import { OverflowTooltip } from "../tooltip/tooltip";
import { TableAlignment, TableFieldType, TableSortDirection } from "./table-constants";

function TableHeaderFilterMenuSelectButton(props) {
    return (
        <button type="button" className="eone-ui-table-header-filter-menu-select-button" title={props.Label} onClick={props.OnClick} data-testid={props.DataTestId}>
            <FontAwesomeIcon icon={props.Icon} />
            <label className="eone-ui-table-header-filter-menu-select-button-label">{props.Label}</label>
        </button>
    );
}

function TableHeaderFilterMenuItem(props) {
    const clickHandler = () => {
        props.OnChange(props.Key);
    };

    return (
        <div className="eone-ui-table-header-filter-menu-item">
            <input type="checkbox" checked={props.Value} onChange={clickHandler} data-testid={props.DataTestId} />
            <label className="eone-ui-table-header-filter-menu-item-label">{props.Label}</label>
        </div>
    );
}

function TableHeaderFilterMenu(props) {
    const getItems = () => {
        return props.FilterValues.map((filterValue, index) => {
            return (
                <TableHeaderFilterMenuItem
                    key={index}
                    Key={filterValue.Key}
                    Label={filterValue.Value}
                    Value={props.SelectedValues.includes(filterValue.Key)}
                    OnChange={props.OnChange}
                    DataTestId={getDataTestId(props.DataTestId, filterValue.Key)} />
            );
        });
    };

    if (!props.Visible) {
        return null;
    }

    return (
        <div className="eone-ui-table-header-filter-menu">
            <div className="eone-ui-table-header-filter-menu-selects">
                <TableHeaderFilterMenuSelectButton
                    Label="Select all"
                    Icon={faCheckSquare}
                    OnClick={props.SelectAll}
                    DataTestId={getDataTestId(props.DataTestId, "select-all")} />
                <TableHeaderFilterMenuSelectButton
                    Label="Select none"
                    Icon={faSquare}
                    OnClick={props.SelectNone}
                    DataTestId={getDataTestId(props.DataTestId, "select-none")} />
            </div>
            <div className="eone-ui-table-header-filter-menu-options">
                {getItems()}
            </div>
            <div className="eone-ui-table-header-filter-menu-buttons">
                <button type="button" className="eone-ui-button-small eone-ui-button-secondary eone-ui-table-header-filter-menu-button" onClick={props.Save} data-testid={getDataTestId(props.DataTestId, "save-button")}>Save</button>
                <button type="button" className="eone-ui-button-small eone-ui-button-tertiary eone-ui-table-header-filter-menu-button" onClick={props.Cancel} data-testid={getDataTestId(props.DataTestId, "cancel-button")}>Cancel</button>
            </div>
        </div>
    );
}

function TableHeaderFilter(props) {
    const [selectedValues, setSelectedValues] = useState([]);
    const dropdownPanelRef = useRef(null);

    const {
        open,
        setOpen,
        close,
        toggleOpen,
        animating,
        triggerRef,
        style,
    } = useDropdownPanel(DropdownPosition.Below, DropdownDirection.Down, DropdownAlignment.AlignLeft, 4, 500);


    useEffect(() => {
        const handleClickOutside = (e) => {
            const outsideTrigger = triggerRef?.current && !triggerRef.current.contains(e.target);
            const outsidePanel = dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target);
            if (outsideTrigger && outsidePanel) {
                close();
            }
        };
        if (open) {
            window.addEventListener("mousedown", handleClickOutside);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open, close]);

    useEffect(() => {
        resetSelectedValues();
    }, [props.FilterValues]);

    const save = () => {
        close();
        props.Save(selectedValues);
    };

    const cancel = () => {
        close();
        resetSelectedValues();
    };

    const resetSelectedValues = () => {
        let newSelectedValues = [];
        if (props.FilterValues) {
            for (let i = 0, len = props.FilterValues.length; i < len; i++) {
                if (props.FilterValues[i].Selected) {
                    newSelectedValues.push(props.FilterValues[i].Key);
                }
            }
        }
        setSelectedValues(newSelectedValues);
    };

    const toggleSelectedValue = (value) => {
        let newSelectedValues = selectedValues.slice(0);
        if (newSelectedValues.includes(value)) {
            newSelectedValues = newSelectedValues.filter(item => item !== value);
        } else {
            newSelectedValues.push(value);
        }
        setSelectedValues(newSelectedValues);
    };

    const selectAll = () => {
        let newSelectedValues = [];
        for (let i = 0, len = props.FilterValues.length; i < len; i++) {
            newSelectedValues.push(props.FilterValues[i].Key);
        }
        setSelectedValues(newSelectedValues);
    };

    const selectNone = () => {
        setSelectedValues([]);
    };

    const toggleMenuOpen = () => {
        let newMenuOpen = !open;
        if (open) {
            resetSelectedValues();
        }
        setOpen(newMenuOpen);
    };

    const getTitle = () => {
        return "Click to filter";
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const minFilterValues = props.FieldType === TableFieldType.Tags ? 1 : 2;
    if (!props.FilterValues || props.FilterValues.length < minFilterValues) {
        return null;
    }

    const icon = selectedValues.length > 0 ? fasFilter : faFilter;
    
    return (
        <div className="eone-ui-table-header-filter" title={getTitle()}>
            <button type="button" className="eone-ui-table-header-filter-icon" onClick={toggleMenuOpen} data-testid={props.DataTestId} ref={triggerRef} aria-label="Click to filter">
                <FontAwesomeIcon icon={icon} />
            </button>
            <DropdownPanel Style={style} Open={open} Close={close} DropdownPanelRef={dropdownPanelRef} >
                <TableHeaderFilterMenu
                    Visible={open}
                    FilterValues={props.FilterValues}
                    SelectedValues={selectedValues}
                    SelectAll={selectAll}
                    SelectNone={selectNone}
                    OnChange={toggleSelectedValue}
                    Save={save}
                    Cancel={cancel}
                    DataTestId={props.DataTestId} />
            </DropdownPanel>
        </div>
    );
}

function TableHeaderSortIndicator(props) {
    const getSortIndicatorIcon = () => {
        switch (props.SortDirection) {
            case TableSortDirection.None:
                return null;
            case TableSortDirection.Ascending:
                return faSortAmountUp;
            case TableSortDirection.Descending:
                return faSortAmountDown;
            default:
                return null;
        }
    };

    const sortIndicatorIcon = getSortIndicatorIcon();

    //#region SC CHANGE
    if(sortIndicatorIcon == null) {
        // note - still want to indicate to the user that this column is sortable
        return (
            <div className="eone-ui-table-header-sort-icon">
                <FontAwesome  Icon={faSort} FixedWidth />
            </div>
        )
    }
    //#endregion

    return (
        <FontAwesome Icon={sortIndicatorIcon} FixedWidth />
    );
}

function EmptyTableHeader(props) {
    // todo - add alignment and data test ids
    return (
        <div className="eone-ui-table-empty-header" style={{ width: props.Width }}>{props.Label}</div>
    );
}

function EmptyTableHeaderRow(props) {
    const getSelectedColumns = () => {
        if (props.SelectedColumns && props.SelectedColumns.length > 0) {
            let columns = [];
            for (let i = 0, len = props.Columns.length; i < len; i++) {
                if (props.Columns[i].Visible !== false && (props.Columns[i].Locked || props.SelectedColumns.includes(props.Columns[i].FieldName))) {
                    columns.push(props.Columns[i]);
                }
            }
            return columns;
        }
        return props.Columns.filter(item => item.Visible !== false);
    };

    const getHeaders = (columns) => {
        const width = (100 / columns.length) + "%";
        return columns.map((column, index) => {
            return (
                <EmptyTableHeader
                    key={index}
                    Label={column.Label}
                    Width={width}
                    Alignment={column.Alignment}
                    DataTestId={props.DataTestId} />
            );
        });
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const columns = getSelectedColumns();
    if (columns.length === 0) {
        return null;
    }

    return (
        <div className="eone-ui-table-empty-header-container">
            {getHeaders(columns)}
        </div>
    );
}

function TableHeaderResizeHandle(props) {
    if (props.Resizable === false) {
        return null;
    }

    const mouseDownHandler = (event) => {
        if (typeof props.OnResizeStart === "function") {
            props.OnResizeStart(props.FieldName, props.MinWidth, props.MaxWidth, event);
        }
    };

    const doubleClickHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof props.OnAutoFit === "function") {
            props.OnAutoFit(props.FieldName);
        }
    };

    return (
        <div
            className="eone-ui-table-header-resize-handle"
            onMouseDown={mouseDownHandler}
            onDoubleClick={doubleClickHandler}
            data-testid={getDataTestId(props.DataTestId, "resize-handle", props.FieldName)}
        />
    );
}

function TableHeader(props) {
    const clickHandler = () => {
        if (typeof props.OnClick === "function") {
            props.OnClick(props.FieldName);
        }
    };

    const filterHandler = (values) => {
        if (typeof props.Filter === "function") {
            props.Filter(props.FieldName, values);
        }
    };

    const getThClass = () => {
        let className = "eone-ui-table-header-data ";
        if (props.Class) {
            className += props.Class;
        }
        if (props.Sticky) {
            className = className + " eone-ui-table-header-data-sticky";
        }
        return className;
    };

    const getContainerClass = () => {
        switch (props.Alignment) {
            case TableAlignment.Center:
                if (props.Filterable) {
                    return "eone-ui-table-header-container-center-filter";
                } else {
                    return "eone-ui-table-header-container-center";
                }
            case TableAlignment.Right:
                return "eone-ui-table-header-container-right";
            default:
                return "eone-ui-table-header-container";
        }
    };

    const getSortTitle = () => {
        switch (props.SortDirection) {
            case TableSortDirection.Ascending:
                return props.Messages?.ClickSortDescending || "Click to sort descending";
            case TableSortDirection.Descending:
                return props.Messages?.ClickSortAscending || "Click to sort ascending";
            default:
                return props.Messages?.ClickSort || "Click to sort";
        }
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const getThStyle = () => {
        const style = {};
        if (props.Width) style.width = props.Width;
        if (props.MinWidth) style.minWidth = props.MinWidth;
        if (props.MaxWidth) style.maxWidth = props.MaxWidth;
        return style;
    };

    const getAriaSort = () => {
        switch (props.SortDirection) {
            case TableSortDirection.Ascending:
                return "ascending";
            case TableSortDirection.Descending:
                return "descending";
            default:
                return undefined;
        }
    };

    if (props.Sortable) {
        return (
            <th className={getThClass()} style={getThStyle()} scope="col" aria-sort={getAriaSort()}>
                <div className={getContainerClass()}>
                    <button type="button" className="eone-ui-table-header-sort-link" onClick={clickHandler} title={getSortTitle()} data-testid={getDataTestId(props.DataTestId, "sort-by", props.Label)}>
                        <OverflowTooltip Label={props.Label}>
                            <span className={"eone-ui-table-header-sorted"}>{props.Label}</span>
                        </OverflowTooltip>
                        <TableHeaderSortIndicator SortDirection={props.SortDirection} />
                    </button>
                    <TableHeaderFilter
                        Visible={props.Filterable}
                        FieldType={props.FieldType}
                        FilterValues={props.FilterValues}
                        Save={filterHandler}
                        DataTestId={getDataTestId(props.DataTestId, "filter", props.Label)} />
                </div>
                <TableHeaderResizeHandle
                    Resizable={props.Resizable}
                    FieldName={props.FieldName}
                    MinWidth={props.MinWidth}
                    MaxWidth={props.MaxWidth}
                    OnResizeStart={props.OnResizeStart}
                    OnAutoFit={props.OnAutoFit}
                    DataTestId={props.DataTestId} />
            </th>
        );
    }
    return (
        <th className={getThClass()} style={getThStyle()} scope="col">
            <div className={getContainerClass()}>
                <OverflowTooltip Label={props.Label}>
                    <div className="pd-table-header-label-container">{props.Label}</div>
                </OverflowTooltip>
                <TableHeaderFilter
                    Visible={props.Filterable}
                    FieldType={props.FieldType}
                    FilterValues={props.FilterValues}
                    Save={filterHandler}
                    DataTestId={getDataTestId(props.DataTestId, "filter", props.Label)} />
            </div>
            <TableHeaderResizeHandle
                Resizable={props.Resizable}
                FieldName={props.FieldName}
                MinWidth={props.MinWidth}
                MaxWidth={props.MaxWidth}
                OnResizeStart={props.OnResizeStart}
                OnAutoFit={props.OnAutoFit}
                DataTestId={props.DataTestId} />
        </th>
    );
}

function TableHeaderRow(props) {
    const sort = (fieldName) => {
        if (typeof props.Sort === "function") {
            props.Sort(fieldName);
        }
    };

    const getSelectedColumns = () => {
        if (props.SelectedColumns && props.SelectedColumns.length > 0) {
            let columns = [];
            for (let i = 0, len = props.Columns.length; i < len; i++) {
                if (props.Columns[i].Locked || props.SelectedColumns.includes(props.Columns[i].FieldName)) {
                    columns.push(props.Columns[i]);
                }
            }
            return columns;
        }
        return props.Columns;
    };


    const getHeaders = () => {
        const columns = getSelectedColumns();
        return columns.map((column, index) => {
            const width = props.GetColumnWidth
                ? (props.GetColumnWidth(column.FieldName) || column.Width)
                : column.Width;

            return (
                <TableHeader
                    index={index}
                    key={index}
                    Sticky={props.Sticky}
                    FirstColumn={index === 0}
                    FieldName={column.FieldName}
                    Visible={column.Visible}
                    Label={column.Label}
                    Width={width}
                    MinWidth={column.MinWidth}
                    MaxWidth={column.MaxWidth}
                    Alignment={column.Alignment}
                    Sortable={column.Sortable && props.Sorting}
                    SortDirection={props.Sorting && column.FieldName === props.Sorting.FieldName ? props.Sorting.Direction : TableSortDirection.None}
                    Filterable={column.Filterable}
                    FieldType={column.FieldType}
                    FilterValues={props.FilterValues[column.FieldName]}
                    Filter={props.Filter}
                    Resizable={column.Resizable}
                    OnResizeStart={props.OnResizeStart}
                    OnAutoFit={props.OnAutoFit}
                    TooltipInfo={column.TooltipInfo}
                    OnClick={sort}
                    DataTestId={props.DataTestId} />
            );
        });
    };

    const getReorderHeader = () => {
        if (props.Reorderable) {
            return (
                <TableHeader Class="eone-ui-table-header-spacer" Sticky={props.Sticky} Width={32} Resizable={false} />
            );
        }
        return (
            <TableHeader Class="eone-ui-table-header-spacer" Sticky={props.Sticky} Width={24} Resizable={false} />
        );
    };

    const getButtonHeader = () => {
        if (props.Buttons && props.Buttons.length > 0) {
            return (
                <TableHeader Class="eone-ui-row-button-table-header" Sticky={props.Sticky} Width={props.ButtonWidth} FirstColumn={false} Resizable={false} />
            );
        }
        return null;
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (!props.Columns || props.Columns.length === 0) {
        return null;
    }

    return (
        <thead>
            <tr className="eone-ui-table-row-header">
                {getReorderHeader()}
                {getHeaders()}
                {getButtonHeader()}
                <TableHeader Class="eone-ui-table-header-spacer" Sticky={props.Sticky} Width={16} Resizable={false} />
            </tr>
        </thead>
    );
}

export { TableHeaderRow, EmptyTableHeaderRow };